import * as React from 'react';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import InputIcon from '@mui/icons-material/Input';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import { GenericDetailedError } from '~/errors/GenericDetailedError';
import { DimensionsService, TimeDimension } from '~/services/dimensions';
import { ApiNFRTemplate } from '~/types/api-types';
import { ExportService, Version } from '~/services/exports';
import { NFRTemplateService } from '~/services/NFR-template';
import { makeStyles } from 'tss-react/mui';
import { Component, forwardRef } from 'react';

const useStyles = makeStyles()({
    dialogStyle: {
       height: '100%',
    },
    xmlDialog: {
        maxHeight: '50vh',
        minHeight: '25vh',
        minWidth: '50vw',
        maxWidth: '50vw',
    },
    '@keyframes fadeInAnimation': {
        from: {opacity: 0, height: 0},
        to: {opacity: 1, height: '100%'},
    },
    nFRTemplateRow: {
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingRight: '0',
        paddingTop: '0',
        paddingBottom: '0',
        border: '1px solid lightgray',
        boxShadow: '0.15rem 0.1rem 0.1rem oklch(from lightgray calc(l * 1.05) c h)',
        borderRadius: '4px',
        marginTop: '0.5rem',
        textAlign: 'center',
        animation: '500ms $fadeInAnimation',
        transition: 'opacity 500ms, height 500ms allow-discrete',
    },
    nfrTemplateDelete: {
        opacity: 0,
        height: 0,
    },
    paddingLeftZero: {
      paddingLeft: '0 !important',
    },
    paddingRightZero: {
        paddingRight: '0 !important',
    },
    input: {
        display: 'none',
    },
    newTemplateButton: {
        transition: 'all 500ms',
        animation: '500ms $fadeInAnimation',
    }
});

interface NFRExportDialogProps {
    exportStarted: () => void;
    importDone: () => void;
    deleteDone: () => void;
    onError: (error: GenericDetailedError) => void;
    Sekretess: boolean;
    classes: ReturnType<typeof useStyles>['classes'];
}

interface NFRExportDialogState {
    open: boolean;
    selectedTimeDimension: string;
    TimeDimensions: TimeDimension[];
    NFRTemplates: ApiNFRTemplate[];
    selectedVersion: string;
    versions: Version[];
    deleteId: Record<number, boolean>;
}

export class NFRDialogRaw extends Component<NFRExportDialogProps, NFRExportDialogState> {
    constructor(props: NFRExportDialogProps) {
        super(props);
        this.state = {
            open: false,
            selectedTimeDimension: '',
            TimeDimensions: [],
            NFRTemplates: [],
            versions: [],
            selectedVersion: '',
            deleteId: {},
        };
    }

    openDialog = async () => {
        this.setState({
            open: true,
        });
        let TimeDimensions = await DimensionsService.getSubmissionYears();
        this.setState({
            TimeDimensions,
            selectedTimeDimension: TimeDimensions[0].name,
        });
    };

    closeDialog = () => {
        this.setState({
            open: false,
            selectedTimeDimension: '',
            TimeDimensions: [],
            NFRTemplates: [],
            deleteId: {},
        });
    };

    createExport = async (templateId: number) => {
        try {
            const exportType = 33; // Hard coded id for NFR export/submission type
            await ExportService.initiateExport(
                this.state.selectedTimeDimension + this.state.selectedVersion,
                JSON.stringify(templateId),
                exportType
            );
            this.props.exportStarted();
        } catch (error) {
            if (error instanceof GenericDetailedError) {
                this.props.onError(error);
            } else {
                console.error(error);
            }
        }
    };

    handleTimeDimensionenChange = (event: SelectChangeEvent<string>) => {
        this.setState({ selectedTimeDimension: event.target.value });
    };

    handleVersionChange = (event: SelectChangeEvent<string>) => {
        this.setState({ selectedVersion: event.target.value });
    };

    deleteTemplate = async (templateId: number) => {
        try {
            this.props.deleteDone();
            this.setState({ deleteId: { ...this.state.deleteId, [templateId]: true }});
            await NFRTemplateService.DeleteNFRTemplate(templateId);
            setTimeout(() => this.setState({
                deleteId: { ...this.state.deleteId, [templateId]: false },
                NFRTemplates: this.state.NFRTemplates.filter(({id}) => id !== templateId)
            }), 600);

        } catch (error) {
            this.setState({ deleteId: {...this.state.deleteId, [templateId]: false }});
            if (error instanceof GenericDetailedError) {
                this.props.onError(error);
            } else {
                console.error(error);
            }
        }
    };

    createImport = async (event: any) => {
        try {
            const timeObj = this.state.TimeDimensions.find(({name}) => name === this.state.selectedTimeDimension);
            const NFRFile = event?.target?.files?.[0];
            if (timeObj == null || NFRFile == null) {
                return;
            }
            this.props.importDone();
            const newTemplate = await NFRTemplateService.importNFRTemplate(timeObj.id, NFRFile);
            this.setState({
                NFRTemplates: [
                    ...this.state.NFRTemplates,
                    newTemplate,
                ]
            });
        } catch (error) {
            if (error instanceof GenericDetailedError) {
                this.props.onError(error);
            } else {
                console.error(error);
            }
        }
    };

    componentDidUpdate = async (_prevProps: NFRExportDialogProps, prevState: NFRExportDialogState) => {
        if (prevState.selectedTimeDimension === this.state.selectedTimeDimension) {
            return;
        }
        const timeObj = this.state.TimeDimensions.find(({name}) => name === this.state.selectedTimeDimension);
        if (!timeObj) {
            return;
        }
        const versions = await ExportService.getTimeDimensionVersions(this.state.selectedTimeDimension);
        this.setState({ versions, selectedVersion: versions.length > 0 ? versions[0].name : '' });
        const NFRTemplates = await NFRTemplateService.getAllNFRTemplatesForSubmission(timeObj.id);
        this.setState({
            NFRTemplates: NFRTemplates?.sort((a, b) => a?.id - b?.id),
        });
    };

    render() {
        const { classes, Sekretess } = this.props;
        return (
            <Dialog open={this.state.open} onClose={this.closeDialog}  classes={{ paper: classes.xmlDialog }} aria-labelledby="form-dialog-title">
                <DialogContent className={classes.dialogStyle}>
                    <form>
                        <Grid container={true} spacing={2}>
                            <Grid item={true} xs={5} md={5} xl={5} className={classes.paddingLeftZero}>
                                <FormControl variant="standard" fullWidth={true}>
                                    <InputLabel htmlFor="submission-year-picker">Submissionsår</InputLabel>
                                    <Select
                                        variant="standard"
                                        onChange={this.handleTimeDimensionenChange}
                                        value={this.state.selectedTimeDimension}
                                        inputProps={{
                                            name: 'year',
                                            id: 'submission-year-picker',
                                        }}
                                    >
                                        {this.state.TimeDimensions.map(({id, name}) => (
                                            <MenuItem value={name} key={id}>
                                                {name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item={true} xs={5} md={5} xl={5} className={classes.paddingRightZero}>
                                <FormControl variant="standard" fullWidth={true}>
                                    <InputLabel htmlFor="submission-year-picker">Version</InputLabel>
                                    <Select
                                        variant="standard"
                                        onChange={this.handleVersionChange}
                                        value={this.state.selectedVersion}
                                        inputProps={{
                                            name: 'version',
                                            id: 'submission-year-version-picker',
                                        }}
                                    >
                                        <MenuItem value="" disabled={true}>
                                            Ingen version
                                        </MenuItem>
                                        {this.state.versions.map(({id, name}) => (
                                            <MenuItem value={name} key={id}>
                                                {name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {this.state.NFRTemplates.map(({id, name}) => (
                                <Grid container={true} item={true} key={id} xs={10} md={10} xl={10} className={`${classes.nFRTemplateRow} ${this.state.deleteId[id] ? classes.nfrTemplateDelete : ''}`}>
                                    <span style={{paddingLeft: '8px'}}>
                                        {name}
                                    </span>
                                    <div>
                                        <IconButton onClick={() => this.deleteTemplate(id)}>
                                            <DeleteIcon titleAccess="Delete" color="error" />
                                        </IconButton>
                                        {<IconButton href={'/api/NFRTemplates/download/' + id} download={name}>
                                            <SaveAltIcon titleAccess="Download" htmlColor="green"/>
                                        </IconButton>}
                                        {Sekretess && <IconButton onClick={() => this.createExport(id)}>
                                            <InputIcon titleAccess="Export" htmlColor="blue"/>
                                        </IconButton>}
                                    </div>
                                </Grid>
                            ))}
                            <Grid item={true} xs={3} md={3} xl={3} className={classes.paddingLeftZero}>
                                <input onChange={this.createImport} className={classes.input} id="outlined-button-file" type="file" />
                                <label htmlFor="outlined-button-file">
                                    <Button className={classes.newTemplateButton} variant="outlined" color="primary" component="span" fullWidth={true}>
                                        Ny Mall
                                    </Button>
                                </label>
                            </Grid>
                        </Grid>
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.closeDialog} color="primary">
                        Stäng
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}


export const NFRDialog = forwardRef<NFRDialogRaw, Omit<NFRExportDialogProps, 'classes'>>((props, ref) => {
    const { classes } = useStyles();

    return <NFRDialogRaw ref={ref} classes={classes} {...props} />;
});
